import Link from "next/link";
import { usePathname } from "next/navigation";
import React from "react";
import {
  MicVocal,
  House,
  BadgePlus,
  CircleDollarSign,
  Gem,
  Code,
  Sparkles,
  Library,
} from "lucide-react";

// Same destinations and order as NAV in Header.js, so the phone menu and the
// desktop pill never disagree about where things live.
const NAV = [
  {
    href: "/",
    label: "Home",
    icon: House,
    match: (p) => p === "/",
  },
  {
    href: "/create",
    label: "Create",
    icon: Sparkles,
    match: (p) => p === "/create",
  },
  {
    href: "/models",
    label: "Voices",
    icon: MicVocal,
    match: (p) => p === "/models" || p.startsWith("/ai-voices"),
  },
  {
    href: "/train",
    label: "Train a voice",
    icon: BadgePlus,
    match: (p) => p === "/train",
  },
  {
    href: "/library",
    label: "Library",
    icon: Library,
    match: (p) => p === "/library" || p === "/history",
    authOnly: true,
  },
  {
    href: "/pricing",
    label: "Pricing",
    icon: Gem,
    match: (p) => p === "/pricing",
  },
];

const MORE = [
  {
    href: "/earn",
    label: "Affiliate Program",
    icon: CircleDollarSign,
  },
  { href: "/docs", label: "API", icon: Code },
];

// Each row fades down a beat after the one above it. The delay is inline:
// a delay class built at runtime is never generated by Tailwind.
const stagger = (index) => ({
  animationDelay: `${index * 40}ms`,
  animationFillMode: "both",
});
const rowAnim =
  "tw-animate-in tw-fade-in-0 tw-slide-in-from-top-2 tw-duration-300";

// Header still passes mobileMenuOpen/isCinematic; it only mounts this while
// open, and F has one look, so neither is read.
const MobileMenu = ({ toggleMobileMenu, isAuthenticated }) => {
  const pathname = usePathname() || "";
  const nav = NAV.filter((item) => !item.authOnly || isAuthenticated);

  return (
    <div className="tw-fixed tw-inset-x-0 tw-bottom-0 tw-top-[calc(4rem_+_var(--card-wall-h,0px))] tw-z-[1000] tw-flex tw-flex-col tw-gap-3 tw-overflow-y-auto tw-bg-white tw-px-4 tw-pb-8 tw-pt-2 tw-text-jam-ink tw-animate-in tw-fade-in-0 tw-duration-200 md:tw-top-20 md:tw-px-8 lg:tw-hidden">
      <nav className="tw-flex tw-flex-col tw-gap-1 tw-rounded-[28px] tw-bg-jam-lav tw-p-[6px]">
        {nav.map((item, index) => {
          const active = item.match(pathname);
          const Icon = item.icon;
          return (
            <Link
              key={item.href}
              href={item.href}
              onClick={toggleMobileMenu}
              style={stagger(index)}
              className={`${rowAnim} tw-flex tw-items-center tw-gap-3 tw-rounded-full tw-px-4 tw-py-3.5 tw-text-base tw-font-extrabold tw-no-underline tw-transition-colors ${
                active
                  ? "tw-bg-white tw-text-jam-purple"
                  : "tw-text-jam-ink hover:tw-text-jam-purple"
              }`}
            >
              <Icon size={20} strokeWidth={2.5} />
              <span>{item.label}</span>
            </Link>
          );
        })}
      </nav>

      <div className="tw-flex tw-flex-col tw-gap-1 tw-px-[6px]">
        {MORE.map((item, index) => {
          const Icon = item.icon;
          return (
            <Link
              key={item.href}
              href={item.href}
              onClick={toggleMobileMenu}
              style={stagger(nav.length + index)}
              className={`${rowAnim} tw-flex tw-items-center tw-gap-3 tw-rounded-full tw-px-4 tw-py-3 tw-text-[15px] tw-font-extrabold tw-text-jam-muted tw-no-underline tw-transition-colors hover:tw-bg-jam-lav hover:tw-text-jam-ink`}
            >
              <Icon size={18} strokeWidth={2.5} />
              <span>{item.label}</span>
            </Link>
          );
        })}
      </div>

      {!isAuthenticated && (
        <div
          style={stagger(nav.length + MORE.length)}
          className={`${rowAnim} tw-mt-1 tw-grid tw-grid-cols-2 tw-gap-2`}
        >
          <Link
            onClick={toggleMobileMenu}
            href={`/login`}
            className="tw-rounded-full tw-bg-jam-lav tw-px-5 tw-py-3.5 tw-text-center tw-text-[15px] tw-font-extrabold tw-text-jam-ink tw-no-underline"
          >
            Log In
          </Link>
          <Link
            onClick={toggleMobileMenu}
            href={`/signup`}
            className="tw-rounded-full tw-bg-jam-ink tw-px-5 tw-py-3.5 tw-text-center tw-text-[15px] tw-font-extrabold tw-text-white tw-no-underline"
          >
            Sign Up
          </Link>
        </div>
      )}
    </div>
  );
};

export default MobileMenu;
